// MACHINERY V2 (Stroje) — openDate <-> native date/time input, quirk included
// (M2.6).
//
// >>> WHY NATIVE INPUTS AND NOT A PICKER COMPONENT. <<<
// v1 renders this field with MUI `DatePicker` + `TimeField`
// (CreateServiceReportPage.tsx:18-19), and §3 bans MUI from the v2 chrome
// outright. The manager pre-approved the fallback for exactly this case: try
// the artboard-faithful shape first, then use native `type="date"` /
// `type="time"` styled in the v2 token idiom rather than round-tripping. That
// is what shipped — the FIELD matches artboards 0702 / 0703 (38px well, mono
// sublabel, teal focus, calendar affordance on the right); the PICKER CHROME
// behind it is the browser's, not MUI's.
//
// >>> THE openDate QUIRK IS DELIBERATE. DO NOT "FIX" IT. <<<
// Data package §7 records it as behaviour to preserve, and all three halves are
// reproduced here:
//   a) THE DATE SHOWS TODAY WHILE THE VALUE IS UNDEFINED. v1 binds
//      `value={moment(field.value)}` (:609) and `moment(undefined)` is NOW, so
//      an untouched form displays today's date over an undefined RHF value.
//      `dateInputValue()` returns today for undefined — same illusion, same
//      undefined value underneath.
//   b) THE TIME IS EMPTY WHEN UNSET. v1 binds the SAME `openDate` field through
//      a second Controller with `value={openDate ? moment(openDate) : null}`
//      (:660), and null renders blank. `timeInputValue()` returns "" for
//      undefined.
//   c) SUBMIT DEFAULTS TO NOW — `data.openDate = data.openDate ||
//      moment().toDate()` (:372), in useSrCreateForm.
// So an untouched form stores "now" and the user sees today's date: consistent,
// and it is what the last four years of reports were filed with. A reader who
// "fixes" (a) makes the control render an empty date that then silently saves
// as today.
//
// TWO SMALLER v1 BEHAVIOURS, ALSO REPRODUCED:
//   · CLEARING THE DATE clears the value. MUI passes null and v1 does
//     `field.onChange(value?.toDate())` (:610) — undefined.
//   · CLEARING THE TIME DOES NOTHING. v1 acts only `if (value)` (:661-671), so
//     an emptied time control leaves openDate exactly as it was.
//   · The picker is future-blocked (`disableFuture` + `maxDate={moment()}`,
//     :612-613); `todayInputValue()` feeds the native `max` attribute. The RHF
//     `validate` rule stays the authority — `max` only stops the calendar UI.
//
// Pure functions: no Redux, no i18n, no DOM. All local-time, matching moment's
// local-time behaviour — a UTC conversion here would shift a late-evening report
// to the next day.
const pad = (n: number): string => (n < 10 ? "0" + n : String(n));

const asDate = (value?: Date | string | null): Date | undefined => {
  if (!value) return undefined;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

/** `YYYY-MM-DD` for `<input type="date">`, local time. */
const dateString = (d: Date): string =>
  d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());

/**
 * Quirk (a): TODAY when the value is undefined, so the control looks exactly
 * like v1's `moment(undefined)` binding while RHF still holds undefined.
 */
export const dateInputValue = (value?: Date | string | null): string =>
  dateString(asDate(value) || new Date());

/** Quirk (b): EMPTY when unset. */
export const timeInputValue = (value?: Date | string | null): string => {
  const d = asDate(value);
  return d ? pad(d.getHours()) + ":" + pad(d.getMinutes()) : "";
};

/** `max` for the date input — the native half of v1's `disableFuture`. */
export const todayInputValue = (): string => dateString(new Date());

/**
 * A new `YYYY-MM-DD` keeping the time-of-day already on the value (or today's,
 * when there is none — MUI derives the same base from `moment(field.value)`).
 * An empty input clears the value, as MUI's null does.
 */
export const applyDateInput = (
  current: Date | string | undefined,
  input: string
): Date | undefined => {
  if (!input) return undefined;
  const parts = input.split("-");
  if (parts.length !== 3) return asDate(current);
  const next = new Date(asDate(current) || new Date());
  next.setFullYear(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return Number.isNaN(next.getTime()) ? undefined : next;
};

/**
 * A new `HH:mm` on the existing date (or today), seconds and milliseconds
 * zeroed — v1's TimeField handler, verbatim. An empty input is IGNORED, which
 * is v1's `if (value)` guard.
 */
export const applyTimeInput = (
  current: Date | string | undefined,
  input: string
): Date | undefined => {
  const base = asDate(current);
  if (!input) return base;
  const parts = input.split(":");
  if (parts.length < 2) return base;
  const next = new Date(base || new Date());
  next.setHours(Number(parts[0]), Number(parts[1]), 0, 0);
  return Number.isNaN(next.getTime()) ? base : next;
};

/** The §7 validation rule: an open date may not be in the future. */
export const isFutureDate = (value?: Date | string | null): boolean => {
  const d = asDate(value);
  return !!d && d.getTime() > Date.now();
};
